import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Config/firebase";
import { COLLECTION_CILINDROS } from "./compactacaoModel";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

function gerarCilindrosPadrao() {
  return Array.from({ length: 20 }, (_, index) => ({
    numero: String(index + 1).padStart(3, "0"),
    peso: "",
    volume: "",
    diametro: "",
    altura: "",
    observacao: "",
    ativo: true,
  }));
}

function normalizar(valor) {
  return String(valor || "").toLowerCase();
}

function numeroCilindro(valor) {
  const numero = Number(String(valor || "").replace(/\D/g, ""));
  return Number.isFinite(numero) ? numero : Number.MAX_SAFE_INTEGER;
}

export default function CadastroTabelaCilindros() {
  const [lista, setLista] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    carregar();
  }, []);

  async function carregar() {
    const snap = await getDocs(
      consultaTenantAtual(query(collection(db, COLLECTION_CILINDROS), orderBy("numero", "asc")))
    );

    const cadastrados = snap.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));

    if (cadastrados.length === 0) {
      setLista(gerarCilindrosPadrao());
      return;
    }

    setLista(cadastrados);
  }

  function alterarLinha(index, campo, valor) {
    setMensagem("");
    setErro("");
    setLista((atual) =>
      atual.map((item, i) =>
        i === index ? { ...item, [campo]: valor } : item
      )
    );
  }

  function acrescentarCilindro() {
    if (salvando) return;
    setMensagem("");
    setErro("");
    setLista((atual) => {
      const maiorNumero = atual.reduce((maior, item) => {
        const numero = Number(String(item.numero || "").replace(/\D/g, ""));
        return Number.isFinite(numero) && numero > maior ? numero : maior;
      }, 0);

      return [
        ...atual,
        {
          numero: String(maiorNumero + 1).padStart(3, "0"),
          peso: "",
          volume: "",
          diametro: "",
          altura: "",
          observacao: "",
          ativo: true,
        },
      ];
    });
    setFiltro("");
  }

  async function salvarTodos() {
    if (salvando) return;

    setSalvando(true);
    setMensagem("");
    setErro("");

    try {
      for (const item of lista) {
        const dados = {
          numero: item.numero || "",
          peso: item.peso || "",
          volume: item.volume || "",
          diametro: item.diametro || "",
          altura: item.altura || "",
          observacao: item.observacao || "",
          ativo: item.ativo ?? true,
          atualizadoEm: new Date(),
        };

        if (item.id) {
          await updateDoc(doc(db, COLLECTION_CILINDROS, item.id), dados);
        } else {
          await addDoc(collection(db, COLLECTION_CILINDROS), aplicarTenantAtual({
            ...dados,
            criadoEm: new Date(),
          }));
        }
      }

      await carregar();
      setMensagem("Cilindros salvos com sucesso.");
    } catch (error) {
      console.error("Erro ao salvar cilindros:", error);
      setErro("Erro ao salvar cilindros. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  const filtrados = useMemo(() => {
    const termo = normalizar(filtro);

    const resultado = !termo
      ? lista
      : lista.filter((item) =>
          [item.numero, item.volume, item.peso, item.altura].some((campo) =>
            contemTextoPesquisa(normalizar(campo), termo)
          )
        );

    return [...resultado].sort((a, b) => {
      const diferenca = numeroCilindro(a.numero) - numeroCilindro(b.numero);
      return diferenca || String(a.numero || "").localeCompare(String(b.numero || ""));
    });
  }, [lista, filtro]);

  return (
    <>
      <Navbar />

      <Box sx={{ mt: "10px", textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700}>
          CILINDROS CBR
        </Typography>
        <Typography variant="body2">
          Tabela padrão de cilindros para ensaios de CBR
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
          <Button variant="contained" onClick={acrescentarCilindro} disabled={salvando}>
            + Acrescentar Cilindro
          </Button>

          <Button variant="contained" color="success" onClick={salvarTodos} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar Alterações"}
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ flex: 1 }}
          />

          <TextField
            size="small"
            label="Qtd"
            value={filtrados.length}
            InputProps={{ readOnly: true }}
            sx={{ width: 120 }}
          />
        </Stack>

        {mensagem && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {mensagem}
          </Alert>
        )}

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead sx={{ backgroundColor: "#f1f5f9" }}>
              <TableRow>
  <TableCell align="center"><strong>Nº</strong></TableCell>
  <TableCell align="center"><strong>Peso</strong></TableCell>
  <TableCell align="center"><strong>Volume</strong></TableCell>
  <TableCell align="center"><strong>Alt. dos Cilindros (cm)</strong></TableCell>
  <TableCell align="center"><strong>Histórico</strong></TableCell>
</TableRow>
            </TableHead>

            <TableBody>
              {filtrados.map((item) => {
                const indexReal = lista.findIndex((x) => x.numero === item.numero);

                return (
                  <TableRow key={item.id || item.numero} hover>
                    <TableCell align="center" sx={{ width: 90 }}>
                      <TextField
                        size="small"
                        value={item.numero}
                        onChange={(e) =>
                          alterarLinha(indexReal, "numero", e.target.value)
                        }
                        inputProps={{ style: { textAlign: "center" } }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ width: 130 }}>
                      <TextField
                        size="small"
                        value={item.peso}
                        onChange={(e) =>
                          alterarLinha(indexReal, "peso", e.target.value)
                        }
                        inputProps={{ style: { textAlign: "center" } }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ width: 130 }}>
                      <TextField
                        size="small"
                        value={item.volume}
                        onChange={(e) =>
                          alterarLinha(indexReal, "volume", e.target.value)
                        }
                        inputProps={{ style: { textAlign: "center" } }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ width: 190 }}>
                      <TextField
                        size="small"
                        value={item.altura}
                        onChange={(e) =>
                          alterarLinha(indexReal, "altura", e.target.value)
                        }
                        inputProps={{ style: { textAlign: "center" } }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ width: 80 }}>
                      <HistoricoCadastro colecao={COLLECTION_CILINDROS} documentoId={item.id} registro={item} />
                    </TableCell>

                
                  </TableRow>
                );
              })}

              {!filtrados.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
  Nenhum cilindro encontrado.
</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </>
  );
}
